import { PackageDeployerFormView } from '../view/PackageDeployerFormView';
import { templatePackageDeployerForm } from '../view/template';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../../framework/BaseWebComponentController';
import { PackageDeployerFormWebComponent } from '../component/PackageDeployerFormWebComponent';
import { PackageDeployerFormRepository } from '../repository/PackageDeployerFormRepository';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { CONNECT_TO_DEPLOYER_OPTIONS, DeployerFormViewMetadata, DeployType } from '../types';
import { DeployerGridWebComponent } from '../../deployerGrid';
import { UpdateServerLocationResponse } from '../../deployerGrid/types/deployer-grid-responses';
import { DeployerGridEvents, DeployerRelayData } from '../../deployerGrid/types';
import { PackageData } from '../../../types/package-data';
import { PackageType } from '../../../packagesGrid/types';

export class PackageDeployerFormController extends BaseWebComponentController {
  public view: PackageDeployerFormView;
  public viewConstructor: typeof PackageDeployerFormView;
  public dataRepository: PackageDeployerFormRepository;
  public dataRepositoryConstructor: typeof PackageDeployerFormRepository;
  public component: PackageDeployerFormWebComponent;
  private updateLocationData: UpdateServerLocationResponse;
  private companyIsSet = false;
  private companySuperAgents: DeployerRelayData[];
  private packageData: PackageData;

  constructor(component: PackageDeployerFormWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: PackageDeployerFormView,
      viewTemplate: templatePackageDeployerForm,
      dataRepositoryConstructor: PackageDeployerFormRepository,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$customCommunicationServerCheckbox,
        event: 'change',
        callback: this.activateCustomCommunicationInput,
      },
      {
        element: this.view.dom.$connectToFormField,
        event: 'selectionChanged',
        callback: this.displayDeployerGridController,
      },
      {
        element: this.view.dom.$deployerGridForESR,
        event: DeployerGridEvents.ROW_SELECTED_EVENT,
        callback: this.handleRowsSelection,
      },
    ];
  }

  private handleRowsSelection(event: WebComponentEvent): void {
    this.view.selectedItemsValidity = event.detail.valid;
    this.view.dom.$connectToFormFieldValidation.validate();
    const selectedRelayCustomHostname = event.detail.selectedRelayCustomHostname;
    const selectedRelayIP = event.detail.selectedRelayIP;
    const selectedRelayName = event.detail.selectedRelayName;
    const selectedRelayId = event.detail.selectedRelayId;

    if (event.detail.selectedRelayName === null || event.detail.selectedRelayIP === null) {
      this.view.setDefaultSelectedRelayField();
      return;
    }

    this.view.selectedRelayData = {
      ip: selectedRelayIP,
      customHostname: selectedRelayCustomHostname,
      _id: selectedRelayId,
    } as DeployerRelayData;
    this.view.dom.$selectedRelayFormFieldValue.innerText = `${selectedRelayName} (${selectedRelayIP})`;
  }

  private activateCustomCommunicationInput(): void {
    this.view.addCustomServerRequiredValidation(this.view.dom.$customCommunicationServerCheckbox.value);
    this.view.dom.$customCommunicationServerField.disabled = !this.view.dom.$customCommunicationServerCheckbox.value;
  }

  private dispatchConnectToSelectionEventController(selected: boolean): void {
    // on edit, we need Relay for FRAT and FRAT Relay to always be disabled no matter the Connect to Deployer type (Endpoint or Bitdefender / GravityZone
    if (
      this.companyIsSet &&
      this.packageData?.type !== PackageType.RelayForFrat &&
      this.packageData?.type !== PackageType.Frat
    ) {
      this.view.dom.$wrapper.dispatchEvent(
        new CustomEvent(DeployerGridEvents.CONNECT_TO_SELECTED_EVENT, {
          bubbles: true,
          composed: true,
          detail: {
            selected: selected,
          },
        })
      );
    }
  }

  private async displayDeployerGridController(): Promise<void> {
    switch (this.view.dom.$connectToFormField.value) {
      case CONNECT_TO_DEPLOYER_OPTIONS[DeployType.BitdefenderCloud].value: {
        this.dispatchConnectToSelectionEventController(false);
        toggleVisibility(this.view.dom.$deployerGridForESR, false);
        toggleVisibility(this.view.dom.$selectedRelayFormField, false);
        this.view.setDefaultCustomCommunicationServer();
        break;
      }
      case CONNECT_TO_DEPLOYER_OPTIONS[DeployType.EndpointSecurityRelay].value: {
        if (!this.view.dom.$deployerGridForESR.started) {
          DeployerGridWebComponent.companyId = this.component.getCompanyId();
          await this.view.dom.$deployerGridForESR.start();
        }

        this.dispatchConnectToSelectionEventController(true);
        toggleVisibility(this.view.dom.$deployerGridForESR, true);
        toggleVisibility(this.view.dom.$selectedRelayFormField, true);
        toggleVisibility(this.view.dom.$customCommunicationSection, false);
        break;
      }
      case CONNECT_TO_DEPLOYER_OPTIONS[DeployType.GravityZoneAppliance].value: {
        this.dispatchConnectToSelectionEventController(false);
        toggleVisibility(this.view.dom.$deployerGridForESR, false);
        toggleVisibility(this.view.dom.$selectedRelayFormField, false);
        toggleVisibility(this.view.dom.$customCommunicationSection, true);
        this.view.setDefaultCustomCommunicationServer();
        break;
      }
    }
    this.view.addConnectToValidators(this.view.dom.$connectToFormField.value as number);
  }

  public async setRelayCheckboxState(state: boolean): Promise<void> {
    if (state) {
      this.view.setConnectToDropdownDefaultOptions();
      this.view.setDefaultConnectOption();
      this.view.dom.$connectToFormField.disabled = true;
    } else {
      this.view.setConnectToOptionsByData();
    }
  }

  public async companyChangeHandler(): Promise<void> {
    this.view.selectedItemsValidity = false;
    const companyId = this.component.getCompanyId();
    if (companyId === undefined) {
      return;
    }
    this.companyIsSet = true;
    this.view.companySuperAgents = await this.getSuperAgentsForCompany(companyId);
    toggleVisibility(this.view.dom.$deployerGridForESR, false);
    toggleVisibility(this.view.dom.$selectedRelayFormField, false);
    DeployerGridWebComponent.companyId = companyId;
    this.view.setConnectToOptionsByData();
    this.view.setDefaultFieldValues();

    if (this.component.isRelay) {
      await this.setRelayCheckboxState(true);
    }

    if (this.view.dom.$deployerGridForESR.started) {
      await this.view.dom.$deployerGridForESR.reload();
    }
  }

  public getDeployerType(): number {
    return this.view.dom.$connectToFormField.value as number;
  }

  private async getSuperAgentsForCompany(companyId: string): Promise<DeployerRelayData[]> {
    return (await this.dataRepository.getSuperAgentsForCompany(companyId)).data;
  }

  async getDataForView(): Promise<DeployerFormViewMetadata> {
    this.updateLocationData = await this.dataRepository.getUpdateServerLocation();
    const companyId = this.component.getCompanyId();
    if (companyId) {
      this.companyIsSet = true;
      this.companySuperAgents = await this.getSuperAgentsForCompany(companyId);
    }

    return {
      updateLocationData: this.updateLocationData,
      context: this.component.formContext,
      companySuperAgents: this.companySuperAgents,
      packageData: this.component.packageData,
      isInstallAgentDeployer: this.component.isInstallAgentDeployer,
    };
  }
}
